import React, { useState, useEffect } from "react";
import { Edit, Delete } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

export default function ResidencyList({ theme }) {
  const [residencies, setResidencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [actionMenu, setActionMenu] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [toast, setToast] = useState({ type: "", message: "" });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editModal, setEditModal] = useState(null); // for edit modal
  const navigate = useNavigate();

  const fetchResidencies = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/residency-status`,
        {
          headers: { 
            "Content-Type": "application/json", 
            Authorization: `Bearer ${localStorage.getItem("token")}` 
          }
        }
      );
      if (!res.ok) throw new Error("Failed to fetch residencies");
      const data = await res.json();

      const mapped = data.map((item) => ({
        id: item.id,
        name: item.residency,
        status: item.status === 1 ? "Active" : "Inactive",
        createdDate: item.created_at
          ? new Date(item.created_at).toLocaleDateString()
          : "N/A",
        updatedDate: item.updated_at
          ? new Date(item.updated_at).toLocaleDateString()
          : "N/A",
      }));

      setResidencies(mapped);
    } catch (err) {
      console.error(err);
      setToast({ type: "error", message: "Failed to load residencies." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResidencies();
  }, []);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ type: "", message: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const filteredResidencies = residencies.filter((r) =>
    r.name.toLowerCase().includes(filter.toLowerCase())
  );

  const toggleMenu = (id) => {
    setActionMenu(actionMenu === id ? null : id);
  };

  // PATCH /api/residency-status/:id
  const updateStatus = async (id, newStatus) => {
    setWorkingId(id);
    setToast({ type: "", message: "" });

    const oldStatus = residencies.find((u) => u.id === id)?.status ?? "Inactive";

    // Instant UI update:
    setResidencies((prev) =>
      prev.map((u) =>
        u.id === id
          ? {
              ...u,
              status: newStatus, // "Active" or "Inactive"
            }
          : u
      )
    );

    try {
      const isActive = newStatus === "Active";
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/residency-status/${id}`,
        {
          method: "PATCH",
          headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}` 
          },
          body: JSON.stringify({ status: isActive }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Status update failed.");
      setToast({ type: "success", message: "Status updated successfully." });
    } catch (err) {
      // Revert on failure
      setResidencies((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, status: oldStatus } : u
        )
      );
      setToast({
        type: "error",
        message: err.message || "Status update failed.",
      });
    } finally {
      setWorkingId(null);
    }
  };

  // PUT /catalog/residency-status/:id - Update residency name
  const updateResidency = async (id, newName) => {
    setWorkingId(id);
    setToast({ type: "", message: "" });

    const oldName = residencies.find((r) => r.id === id)?.name ?? "";

    // Instant UI update:
    setResidencies((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              name: newName,
              updatedDate: new Date().toLocaleDateString(),
            }
          : r
      )
    );

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/residency-status/${id}`,
        {
          method: "PUT",
          headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}` 
          },
          body: JSON.stringify({ residency: newName }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Update failed.");
      setToast({ type: "success", message: "Residency updated successfully." });
    } catch (err) {
      // Revert on failure
      setResidencies((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, name: oldName } : r
        )
      );
      setToast({
        type: "error",
        message: err.message || "Update failed.",
      });
    } finally {
      setWorkingId(null);
      setEditModal(null);
    }
  };

  // DELETE /catalog/residency-status/:id
  const deleteResidency = async (id) => {
    setWorkingId(id);
    setToast({ type: "", message: "" });

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/residency-status/${id}`,
        { 
          method: "DELETE",
          headers: { 
            "Content-Type": "application/json", 
            Authorization: `Bearer ${localStorage.getItem("token")}` 
          }
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Delete failed.");
      setToast({ type: "success", message: "Residency deleted successfully." });
      setResidencies((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setToast({ type: "error", message: err.message || "Failed to delete." });
    } finally {
      setWorkingId(null);
      setConfirmDelete(null);
    }
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newName = formData.get('residency').trim();
    
    if (!newName) {
      setToast({ type: "error", message: "Residency name cannot be empty." });
      return;
    }
    
    updateResidency(editModal.id, newName);
  };

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center p-10">
        <div className="text-lg text-gray-600">Loading residencies…</div>
      </div>
    );
  }

  const isModalOpen = editModal || confirmDelete;

  return (
    <div className="relative w-full max-w-full">
      {/* Main content with conditional blur */}
      <div className={`p-6 bg-white rounded shadow transition-all duration-300 ${isModalOpen ? 'blur-sm' : ''}`}>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Residency List</h2>
          <p className="text-gray-600">Browse All Residencies</p>
        </div>

        <div className="flex items-center justify-between mb-6">
          <input
            type="text"
            className="border border-gray-300 px-4 py-2 rounded-lg w-1/3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Type to filter residencies..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className="text-sm text-gray-600 font-medium">
            Showing {filteredResidencies.length} of {residencies.length} entries
          </span>
        </div>

        <div className="overflow-auto border border-gray-200 rounded-lg" style={{ maxHeight: '600px' }}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="p-4 border-b border-gray-200 text-left font-semibold text-gray-700">#</th>
                <th className="p-4 border-b border-gray-200 text-left font-semibold text-gray-700">Residency</th>
                <th className="p-4 border-b border-gray-200 text-left font-semibold text-gray-700">Status</th>
                <th className="p-4 border-b border-gray-200 text-left font-semibold text-gray-700">Created</th>
                <th className="p-4 border-b border-gray-200 text-left font-semibold text-gray-700">Updated</th>
                <th className="p-4 border-b border-gray-200 text-center font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredResidencies.map((res, index) => (
                <tr key={res.id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-4 border-b border-gray-200 text-gray-600">{index + 1}</td>
                  <td className="p-4 border-b border-gray-200">
                    <span className="text-blue-600 font-medium">{res.name}</span>
                  </td>
                  <td className="p-4 border-b border-gray-200">
                    <select
                      value={res.status}
                      onChange={(e) => updateStatus(res.id, e.target.value)}
                      disabled={workingId === res.id}
                      className={`px-3 py-1 rounded text-xs font-semibold border focus:outline-none focus:ring-1 ${
                        res.status === "Active"
                          ? "text-green-800 bg-green-100 border-green-400 focus:ring-green-500"
                          : "text-red-800 bg-red-100 border-red-400 focus:ring-red-500"
                      }`}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </td>
                  <td className="p-4 border-b border-gray-200 text-gray-600">{res.createdDate}</td>
                  <td className="p-4 border-b border-gray-200 text-gray-600">{res.updatedDate}</td>
                  <td className="p-4 border-b border-gray-200 text-center relative">
                    <button
                      onClick={() => toggleMenu(res.id)}
                      className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      ⚙
                    </button>
                    {actionMenu === res.id && (
                      <div
                        className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-50 top-full"
                        onMouseLeave={() => setActionMenu(null)}
                      >
                        <button
                          onClick={() => {
                            setEditModal({ id: res.id, name: res.name });
                            setActionMenu(null);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 text-blue-600 flex items-center gap-2 transition-colors"
                          disabled={workingId === res.id}
                        >
                          <Edit size={16} /> Edit
                        </button>
                        <button
                          onClick={() => {
                            setConfirmDelete({ id: res.id, name: res.name });
                            setActionMenu(null);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2 transition-colors"
                          disabled={workingId === res.id}
                        >
                          <Delete size={16} /> Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filteredResidencies.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-8 text-center text-gray-500 italic bg-gray-50"
                  >
                    No residencies found matching your filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toast container (fixed top-right) */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        <AnimatePresence>
          {toast.message && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.25 }}
              className={`max-w-xs w-full ${
                toast.type === "error"
                  ? "bg-red-50 border-red-300 text-red-700"
                  : "bg-green-50 border-green-300 text-green-700"
              } border rounded-lg shadow-lg overflow-hidden`}
            >
              <div className="flex items-start p-4">
                <div className="flex-shrink-0 mr-3">
                  {toast.type === "error" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.366-1.029 1.97-1.029 2.336 0l.823 2.32a1 1 0 00.95.69h2.462c1.079 0 1.527 1.378.739 2.028l-1.992 1.45a1 1 0 00-.364 1.118l.823 2.32c.366 1.029-.95 1.887-1.74 1.118l-1.992-1.45a1 1 0 00-1.176 0l-1.992 1.45c-.789.77-2.106-.089-1.74-1.118l.823-2.32a1 1 0 00-.364-1.118L2.72 8.137c-.788-.65-.34-2.028.739-2.028h2.462a1 1 0 00.95-.69l.823-2.32z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1 text-sm font-medium">{toast.message}</div>
                <button
                  className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                  onClick={() => setToast({ type: "", message: "" })}
                  aria-label="Dismiss toast"
                >
                  ×
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editModal && (
          <motion.div
            className="fixed inset-0 bg-gray-800 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl mx-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-xl font-semibold mb-4 text-gray-900">Edit Residency</h3>
              <form onSubmit={handleEditSubmit}>
                <div className="mb-6">
                  <label htmlFor="residency" className="block text-sm font-medium text-gray-700 mb-2">
                    Residency Name
                  </label>
                  <input
                    type="text"
                    id="residency"
                    name="residency"
                    defaultValue={editModal.name}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter residency name"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setEditModal(null)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
                    disabled={workingId === editModal.id}
                  >
                    {workingId === editModal.id ? "Updating..." : "Update"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom Confirm Delete Modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            className="fixed inset-0 bg-gray-800 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl mx-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-xl font-semibold mb-4 text-gray-900">Confirm Delete</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-red-600">{confirmDelete.name}</span>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteResidency(confirmDelete.id)}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50"
                  disabled={workingId === confirmDelete.id}
                >
                  {workingId === confirmDelete.id ? "Deleting..." : "Delete"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}